import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import {
  createCrmActivity,
  createCrmContact,
  extractCrmIntake,
  extractCrmIntakeFile,
  getCrmJobSummary,
  listCrmActivities,
  listCrmContacts,
  listCrmJobs,
  patchCrmContact,
  patchCrmActivity,
} from "../../api/crm";
import { createJob, listJobs } from "../../api/jobs";
import { standardizeCapitalization } from "../../utils/textFormat";

const TABS = ["overview", "pipeline", "contacts", "activities"];
const PIPELINE_STAGES = ["lead", "contacted", "scheduled", "closed"];

function contactFormFromIntake(contact = {}) {
  return {
    name: String(contact.name || ""),
    email: String(contact.email || ""),
    phone: String(contact.phone || ""),
    company: String(contact.company || ""),
    status: String(contact.status || "lead"),
    notes: String(contact.notes || ""),
  };
}

function activityFormFromIntake(activity = {}) {
  return {
    contact_id: "",
    kind: String(activity.kind || "follow_up"),
    summary: String(activity.summary || ""),
    due_at: String(activity.due_at || ""),
  };
}

function buildIntakeJobNotes(result, sourceText) {
  const contact = result?.contact || {};
  const job = result?.job || {};
  const activity = result?.activity || {};
  const rows = [
    ["Property owner", contact.name],
    ["Email", contact.email],
    ["Phone", contact.phone],
    ["Company", contact.company],
    ["Property address", job.address],
    ["Claim/policy", job.claim_number],
    ["Loss type", job.loss_type],
    ["Follow-up kind", activity.kind],
    ["Follow-up summary", activity.summary],
    ["Follow-up due", activity.due_at],
  ]
    .filter(([, value]) => String(value || "").trim())
    .map(([label, value]) => `${label}: ${String(value).trim()}`);

  const raw = String(sourceText || "").trim();
  if (raw) {
    rows.push("Source intake:");
    rows.push(raw);
  }
  return rows.join("\n");
}

export default function CrmWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = React.useState("overview");
  const [jobs, setJobs] = React.useState([]);
  const [jobId, setJobId] = React.useState("");
  const [summary, setSummary] = React.useState(null);
  const [contacts, setContacts] = React.useState([]);
  const [activities, setActivities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [contactForm, setContactForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    status: "lead",
    notes: "",
  });
  const [activityForm, setActivityForm] = React.useState({
    contact_id: "",
    kind: "follow_up",
    summary: "",
    due_at: "",
  });
  const [intakeText, setIntakeText] = React.useState("");
  const [intakeFileName, setIntakeFileName] = React.useState("");
  const [intakeResult, setIntakeResult] = React.useState(null);
  const [extracting, setExtracting] = React.useState(false);
  const [dragContactId, setDragContactId] = React.useState("");
  const [dragOverStage, setDragOverStage] = React.useState("");

  const loadAll = React.useCallback(async (selectedJobId = "") => {
    setLoading(true);
    setError("");
    try {
      const [crmJobs, rawJobs] = await Promise.all([listCrmJobs(), listJobs({ limit: 200, offset: 0 })]);
      const mergedJobs = Array.isArray(rawJobs) ? rawJobs : [];
      const candidateJobId =
        selectedJobId ||
        searchParams.get("jobId") ||
        (mergedJobs[0] && String(mergedJobs[0].id)) ||
        "";
      setJobs(mergedJobs);
      setJobId(candidateJobId);
      if (candidateJobId) {
        const [sum, c, a] = await Promise.all([
          getCrmJobSummary(candidateJobId),
          listCrmContacts(candidateJobId),
          listCrmActivities(candidateJobId),
        ]);
        setSummary(sum);
        setContacts(c);
        setActivities(a);
      } else {
        setSummary(null);
        setContacts([]);
        setActivities([]);
      }
      void crmJobs; // kept for future board enhancements
    } catch (e) {
      const status = Number(e?.status || 0);
      if (status === 401) {
        setError("Your session expired. Please sign in again.");
        navigate(`/login?next=${encodeURIComponent(`/app/crm${window.location.search || ""}`)}`, { replace: true });
        return;
      }
      setError(e?.message || "Failed to load CRM data");
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams]);

  React.useEffect(() => {
    const incoming = (searchParams.get("tab") || "").toLowerCase();
    if (TABS.includes(incoming)) {
      setTab(incoming);
    }
  }, [searchParams]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleJobChange = async (nextJobId) => {
    const id = String(nextJobId || "");
    const next = new URLSearchParams(searchParams);
    if (id) next.set("jobId", id);
    else next.delete("jobId");
    setSearchParams(next, { replace: true });
    await loadAll(id);
  };

  const handleTab = (nextTab) => {
    const t = String(nextTab || "overview").toLowerCase();
    const finalTab = TABS.includes(t) ? t : "overview";
    setTab(finalTab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", finalTab);
    setSearchParams(next, { replace: true });
  };

  const submitContact = async () => {
    if (!jobId) return;
    if (!String(contactForm.name || "").trim()) {
      setError("Contact name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createCrmContact(jobId, contactForm);
      setContactForm({ name: "", email: "", phone: "", company: "", status: "lead", notes: "" });
      await loadAll(jobId);
      handleTab("contacts");
    } catch (e) {
      setError(e?.message || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const submitActivity = async () => {
    if (!jobId) return;
    if (!String(activityForm.summary || "").trim()) {
      setError("Activity summary is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createCrmActivity(jobId, activityForm);
      setActivityForm({ contact_id: "", kind: "follow_up", summary: "", due_at: "" });
      await loadAll(jobId);
      handleTab("activities");
    } catch (e) {
      setError(e?.message || "Failed to save activity");
    } finally {
      setSaving(false);
    }
  };

  const handleIntakeFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setError("");
    setIntakeResult(null);
    setIntakeFileName(file.name || "");
    setContactForm(contactFormFromIntake());
    setActivityForm(activityFormFromIntake());
    if (file.size > 12 * 1024 * 1024) {
      setError("That file is too large for Smart Intake. Use a file no larger than 12 MB.");
      return;
    }
    setExtracting(true);
    try {
      const result = await extractCrmIntakeFile(file);
      setIntakeText(String(result?.source_text || ""));
      setIntakeResult(result);
      setContactForm(contactFormFromIntake(result?.contact || {}));
      setActivityForm(activityFormFromIntake(result?.activity || {}));
    } catch (e) {
      setError(e?.message || "Could not read that Smart Intake file.");
    } finally {
      setExtracting(false);
      event.target.value = "";
    }
  };

  const runIntakeExtraction = async () => {
    const text = String(intakeText || "").trim();
    if (!text) {
      setError("Paste lead, claim, email, or contact data before extracting.");
      return;
    }
    setExtracting(true);
    setError("");
    try {
      const result = await extractCrmIntake({ text });
      setIntakeResult(result);
      const contact = result?.contact || {};
      setContactForm(contactFormFromIntake(contact));
      const activity = result?.activity || {};
      setActivityForm(activityFormFromIntake(activity));
    } catch (e) {
      setError(e?.message || "Failed to extract CRM intake");
    } finally {
      setExtracting(false);
    }
  };

  const clearSmartIntake = () => {
    setIntakeText("");
    setIntakeFileName("");
    setIntakeResult(null);
    setError("");
  };

  const applyIntakeToContact = () => {
    const contact = intakeResult?.contact || {};
    setContactForm(contactFormFromIntake(contact));
  };

  const createExtractedJob = async () => {
    const name = standardizeCapitalization(intakeResult?.job?.name || "");
    if (!name) {
      setError("No job name was extracted.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await createJob({
        name,
        notes: buildIntakeJobNotes(intakeResult, intakeText),
        intake_source: String(intakeText || "").trim(),
        intake_json: intakeResult || {},
      });
      const nextJobId = String(created?.id || "");
      const contact = intakeResult?.contact || {};
      const contactName = String(contact?.name || "").trim();
      if (nextJobId && contactName) {
        await createCrmContact(nextJobId, {
          name: standardizeCapitalization(contactName),
          email: contact.email || "",
          phone: contact.phone || "",
          company: contact.company || "",
          status: contact.status || "lead",
          notes: contact.notes || "",
        });
      }
      if (nextJobId) {
        await handleJobChange(nextJobId);
        handleTab("contacts");
      } else {
        await loadAll(jobId);
      }
    } catch (e) {
      setError(e?.message || "Failed to create extracted job");
    } finally {
      setSaving(false);
    }
  };

  const toggleActivity = async (activity) => {
    setSaving(true);
    setError("");
    try {
      await patchCrmActivity(activity.id, { completed: !Boolean(activity.completed) });
      await loadAll(jobId);
    } catch (e) {
      setError(e?.message || "Failed to update activity");
    } finally {
      setSaving(false);
    }
  };

  const moveContactStage = async (contact, nextStatus) => {
    if (!contact?.id) return;
    const status = String(nextStatus || "").trim().toLowerCase();
    if (!PIPELINE_STAGES.includes(status)) return;
    setSaving(true);
    setError("");
    try {
      await patchCrmContact(contact.id, { status });
      await loadAll(jobId);
      handleTab("pipeline");
    } catch (e) {
      setError(e?.message || "Failed to update contact stage");
    } finally {
      setSaving(false);
    }
  };

  const onDragStartContact = (event, contact) => {
    const id = String(contact?.id || "");
    if (!id) return;
    setDragContactId(id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", id);
  };

  const onDragEndContact = () => {
    setDragContactId("");
    setDragOverStage("");
  };

  const onDragOverStage = (event, stage) => {
    event.preventDefault();
    if (!PIPELINE_STAGES.includes(stage)) return;
    event.dataTransfer.dropEffect = "move";
    setDragOverStage(stage);
  };

  const onDropToStage = async (event, stage) => {
    event.preventDefault();
    const targetStage = String(stage || "").toLowerCase();
    setDragOverStage("");
    if (!PIPELINE_STAGES.includes(targetStage)) return;
    const idFromEvent = String(event.dataTransfer.getData("text/plain") || "");
    const contactId = idFromEvent || dragContactId;
    if (!contactId) return;
    const contact = contacts.find((c) => String(c?.id || "") === contactId);
    if (!contact) return;
    const current = String(contact?.status || "lead").toLowerCase();
    if (current === targetStage) return;
    await moveContactStage(contact, targetStage);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">CRM Workspace</h1>
          <p className="mt-1 text-sm text-slate-500">Track client contacts, follow-ups, and job activity in one place.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <select
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
              value={jobId}
              onChange={(e) => handleJobChange(e.target.value)}
            >
              <option value="">Select a job</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {standardizeCapitalization(j.name || "") || "(untitled)"}
                </option>
              ))}
            </select>
            <Link to="/app/jobs">
              <Button variant="secondary" className="w-full">Jobs</Button>
            </Link>
            <Link to="/app/build">
              <Button variant="ghost" className="w-full">Build</Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant={tab === "overview" ? "primary" : "secondary"} onClick={() => handleTab("overview")}>Overview</Button>
            <Button variant={tab === "pipeline" ? "primary" : "secondary"} onClick={() => handleTab("pipeline")}>Pipeline</Button>
            <Button variant={tab === "contacts" ? "primary" : "secondary"} onClick={() => handleTab("contacts")}>Contacts</Button>
            <Button variant={tab === "activities" ? "primary" : "secondary"} onClick={() => handleTab("activities")}>Activities</Button>
          </div>
        </CardBody>
      </Card>

      {loading ? <p className="text-sm text-slate-500">Loading CRM...</p> : null}

      {!loading && tab !== "contacts" ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Smart Intake</h2>
            <p className="mt-1 text-sm text-slate-500">
              Paste lead, claim, email, or contact data here, or upload a text-based file. The CRM will extract fields for a new contact, job, and follow-up.
            </p>
          </CardHeader>
          <CardBody className="space-y-3">
            {intakeFileName ? <p className="text-xs text-slate-500">Loaded file: {intakeFileName}</p> : null}
            <textarea
              className="min-h-36 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
              placeholder={"Paste intake data here...\nExample: Name, phone, email, property address, claim number, loss type, notes."}
              value={intakeText}
              onChange={(e) => {
                setIntakeText(e.target.value);
                setIntakeResult(null);
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex h-10 cursor-pointer items-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Upload Data
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.vcf,.json,.md,.log"
                  onChange={handleIntakeFile}
                />
              </label>
              <Button onClick={runIntakeExtraction} disabled={extracting || !String(intakeText || "").trim()}>
                {extracting ? "Extracting..." : "Extract Fields"}
              </Button>
              <Button variant="ghost" onClick={clearSmartIntake} disabled={extracting && !intakeText && !intakeResult}>
                Clear Intake
              </Button>
              {intakeResult ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    applyIntakeToContact();
                    handleTab("contacts");
                  }}
                >
                  Apply and Open Contact Form
                </Button>
              ) : null}
              {intakeResult?.job?.name ? (
                <Button variant="secondary" onClick={createExtractedJob} disabled={saving}>
                  Create Job: {intakeResult.job.name}
                </Button>
              ) : null}
            </div>
            {intakeResult ? (
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact</p>
                  <dl className="mt-2 space-y-1 text-sm text-slate-700">
                    <div><dt className="inline font-medium text-slate-900">Name:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.contact?.name || "") || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Email:</dt> <dd className="inline">{intakeResult.contact?.email || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Phone:</dt> <dd className="inline">{intakeResult.contact?.phone || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Company:</dt> <dd className="inline">{intakeResult.contact?.company || "Not found"}</dd></div>
                  </dl>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job</p>
                  <dl className="mt-2 space-y-1 text-sm text-slate-700">
                    <div><dt className="inline font-medium text-slate-900">Name:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.job?.name || "") || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Address:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.job?.address || "") || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Claim:</dt> <dd className="inline">{intakeResult.job?.claim_number || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Loss:</dt> <dd className="inline">{intakeResult.job?.loss_type || "Not found"}</dd></div>
                  </dl>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Activity</p>
                  <dl className="mt-2 space-y-1 text-sm text-slate-700">
                    <div><dt className="inline font-medium text-slate-900">Kind:</dt> <dd className="inline">{intakeResult.activity?.kind || "note"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Summary:</dt> <dd className="inline">{intakeResult.activity?.summary || "Not found"}</dd></div>
                    <div><dt className="inline font-medium text-slate-900">Due:</dt> <dd className="inline">{intakeResult.activity?.due_at || "Not found"}</dd></div>
                  </dl>
                  {(intakeResult.warnings || []).length ? (
                    <ul className="mt-2 list-disc pl-4 text-xs text-amber-700">
                      {intakeResult.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "overview" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Overview</h2></CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Contacts</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.contacts ?? 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Activities</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.activities ?? 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Open Tasks</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.open_activities ?? 0}</p>
            </div>
            <div className="sm:col-span-3">
              <h3 className="text-sm font-semibold text-slate-800">Next Activities</h3>
              <ul className="mt-2 space-y-2">
                {(summary?.next_activities || []).length < 1 ? (
                  <li className="text-sm text-slate-500">No upcoming activities.</li>
                ) : (
                  summary.next_activities.map((a) => (
                    <li key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      {a.summary} {a.due_at ? <span className="text-slate-500">(due {a.due_at})</span> : null}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "pipeline" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Pipeline</h2></CardHeader>
          <CardBody>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {PIPELINE_STAGES.map((stage) => {
                const stageItems = contacts.filter((c) => String(c?.status || "lead").toLowerCase() === stage);
                return (
                  <div
                    key={stage}
                    className={`rounded-xl border p-3 transition ${
                      dragOverStage === stage ? "border-red-400 bg-red-50/70" : "border-slate-200 bg-slate-50"
                    }`}
                    onDragOver={(e) => onDragOverStage(e, stage)}
                    onDrop={(e) => onDropToStage(e, stage)}
                    onDragLeave={() => setDragOverStage((prev) => (prev === stage ? "" : prev))}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold capitalize text-slate-900">{stage}</h3>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-600">{stageItems.length}</span>
                    </div>
                    <div className="space-y-2">
                      {stageItems.length < 1 ? (
                        <p className="text-xs text-slate-500">No contacts</p>
                      ) : (
                        stageItems.map((c) => {
                          const idx = PIPELINE_STAGES.indexOf(stage);
                          const canBack = idx > 0;
                          const canNext = idx < PIPELINE_STAGES.length - 1;
                          return (
                            <div
                              key={c.id}
                              className={`rounded-lg border bg-white p-2 ${
                                dragContactId === c.id ? "border-red-300 shadow-sm" : "border-slate-200"
                              }`}
                              draggable
                              onDragStart={(e) => onDragStartContact(e, c)}
                              onDragEnd={onDragEndContact}
                            >
                              <p className="text-sm font-medium text-slate-900">{c.name}</p>
                              <p className="text-xs text-slate-500">{c.company || c.email || c.phone || "No details yet"}</p>
                              <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">Drag to move stage</p>
                              <div className="mt-2 flex gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={!canBack || saving}
                                  onClick={() => moveContactStage(c, PIPELINE_STAGES[idx - 1])}
                                >
                                  Back
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={!canNext || saving}
                                  onClick={() => moveContactStage(c, PIPELINE_STAGES[idx + 1])}
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "contacts" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Contacts</h2></CardHeader>
          <CardBody className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Smart Intake</h3>
                  <p className="text-xs text-slate-500">
                    Paste a lead form, email, claim note, or upload a text/CSV/VCF file. Review the fields before saving.
                  </p>
                </div>
                {intakeFileName ? <span className="text-xs text-slate-500">Loaded: {intakeFileName}</span> : null}
              </div>
              <textarea
                className="mt-3 min-h-32 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                placeholder={"Paste intake data here...\nExample: Name, phone, email, property address, claim number, loss type, notes."}
                value={intakeText}
                onChange={(e) => {
                  setIntakeText(e.target.value);
                  setIntakeResult(null);
                }}
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="inline-flex h-10 cursor-pointer items-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Upload Data
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.vcf,.json,.md,.log"
                    onChange={handleIntakeFile}
                  />
                </label>
                <Button onClick={runIntakeExtraction} disabled={extracting || !String(intakeText || "").trim()}>
                  {extracting ? "Extracting..." : "Extract Fields"}
                </Button>
                <Button variant="ghost" onClick={clearSmartIntake} disabled={extracting && !intakeText && !intakeResult}>
                  Clear Intake
                </Button>
                {intakeResult ? (
                  <Button variant="secondary" onClick={applyIntakeToContact}>
                    Apply to Contact
                  </Button>
                ) : null}
                {intakeResult?.job?.name ? (
                  <Button variant="secondary" onClick={createExtractedJob} disabled={saving}>
                    Create Job: {intakeResult.job.name}
                  </Button>
                ) : null}
              </div>
              {intakeResult ? (
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact</p>
                    <dl className="mt-2 space-y-1 text-sm text-slate-700">
                      <div><dt className="inline font-medium text-slate-900">Name:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.contact?.name || "") || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Email:</dt> <dd className="inline">{intakeResult.contact?.email || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Phone:</dt> <dd className="inline">{intakeResult.contact?.phone || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Company:</dt> <dd className="inline">{intakeResult.contact?.company || "Not found"}</dd></div>
                    </dl>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job</p>
                    <dl className="mt-2 space-y-1 text-sm text-slate-700">
                      <div><dt className="inline font-medium text-slate-900">Name:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.job?.name || "") || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Address:</dt> <dd className="inline">{standardizeCapitalization(intakeResult.job?.address || "") || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Claim:</dt> <dd className="inline">{intakeResult.job?.claim_number || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Loss:</dt> <dd className="inline">{intakeResult.job?.loss_type || "Not found"}</dd></div>
                    </dl>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Activity</p>
                    <dl className="mt-2 space-y-1 text-sm text-slate-700">
                      <div><dt className="inline font-medium text-slate-900">Kind:</dt> <dd className="inline">{intakeResult.activity?.kind || "note"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Summary:</dt> <dd className="inline">{intakeResult.activity?.summary || "Not found"}</dd></div>
                      <div><dt className="inline font-medium text-slate-900">Due:</dt> <dd className="inline">{intakeResult.activity?.due_at || "Not found"}</dd></div>
                    </dl>
                    {(intakeResult.warnings || []).length ? (
                      <ul className="mt-2 list-disc pl-4 text-xs text-amber-700">
                        {intakeResult.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} />
              <Input placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} />
              <Input placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} />
              <Input placeholder="Company" value={contactForm.company} onChange={(e) => setContactForm((p) => ({ ...p, company: e.target.value }))} />
              <Input className="sm:col-span-2" placeholder="Notes" value={contactForm.notes} onChange={(e) => setContactForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <Button onClick={submitContact} disabled={!jobId || saving}>{saving ? "Saving..." : "Add Contact"}</Button>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-600">Name</th>
                    <th className="px-3 py-2 text-left text-slate-600">Email</th>
                    <th className="px-3 py-2 text-left text-slate-600">Phone</th>
                    <th className="px-3 py-2 text-left text-slate-600">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length < 1 ? (
                    <tr><td colSpan={4} className="px-3 py-3 text-slate-500">No contacts yet.</td></tr>
                  ) : (
                    contacts.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-900">{c.name}</td>
                        <td className="px-3 py-2 text-slate-700">{c.email || ""}</td>
                        <td className="px-3 py-2 text-slate-700">{c.phone || ""}</td>
                        <td className="px-3 py-2 text-slate-700">{c.company || ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "activities" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Activities</h2></CardHeader>
          <CardBody className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                value={activityForm.contact_id}
                onChange={(e) => setActivityForm((p) => ({ ...p, contact_id: e.target.value }))}
              >
                <option value="">No contact (job-level)</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <Input placeholder="Kind (follow_up, note, call)" value={activityForm.kind} onChange={(e) => setActivityForm((p) => ({ ...p, kind: e.target.value }))} />
              <Input className="sm:col-span-2" placeholder="Summary" value={activityForm.summary} onChange={(e) => setActivityForm((p) => ({ ...p, summary: e.target.value }))} />
              <Input type="datetime-local" value={activityForm.due_at} onChange={(e) => setActivityForm((p) => ({ ...p, due_at: e.target.value }))} />
            </div>
            <Button onClick={submitActivity} disabled={!jobId || saving}>{saving ? "Saving..." : "Add Activity"}</Button>
            <ul className="space-y-2">
              {activities.length < 1 ? (
                <li className="text-sm text-slate-500">No activities yet.</li>
              ) : (
                activities.map((a) => (
                  <li key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-900">{a.summary}</p>
                        <p className="text-slate-500">{a.kind}{a.due_at ? ` - due ${a.due_at}` : ""}</p>
                      </div>
                      <Button size="sm" variant={a.completed ? "secondary" : "primary"} onClick={() => toggleActivity(a)} disabled={saving}>
                        {a.completed ? "Reopen" : "Mark Done"}
                      </Button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
